import { EnumTagSemanticoDTOElementoRicerca } from '@framework/api-client/api/users/schemas/enumTagSemanticoDTOElementoRicerca.ts';
import { EnumTagSemanticoDTOTipo } from '@framework/api-client/api/users/schemas/enumTagSemanticoDTOTipo.ts';
import { type TagSemanticoDTO } from '@framework/api-client/api/users/schemas/tagSemanticoDTO.ts';
import { isEmptySearchValue } from '@framework/ui/components/search/isEmptySearchValue.ts';
import {
  type ISemanticFieldSearch,
  SemanticFieldSearchOperator,
} from './ISemanticFieldSearch.ts';
import {
  searchableFieldFromName,
  searchableFieldToName,
} from './mapSemanticTagsToSearchableFields.ts';

function getSemanticTagFilterOperator(
  semanticTag: TagSemanticoDTO,
  values: Record<string, any>,
): ISemanticFieldSearch[] {
  if (!semanticTag.nome) return [];

  if (
    semanticTag.elementoRicerca ===
    EnumTagSemanticoDTOElementoRicerca.RICERCA_CON_MENU_TENDINA
  ) {
    const value = values[semanticTag.nome];
    if (isEmptySearchValue(value)) return [];
    return [
      {
        chiave: semanticTag.id,
        operatore: SemanticFieldSearchOperator.EQUALS,
        valore: value,
      },
    ];
  }

  if (
    semanticTag.elementoRicerca ===
    EnumTagSemanticoDTOElementoRicerca.RICERCA_LIBERA
  ) {
    const value = values[semanticTag.nome];
    if (isEmptySearchValue(value)) return [];
    return [
      {
        chiave: semanticTag.id,
        operatore:
          semanticTag.tipo === EnumTagSemanticoDTOTipo.TEXT
            ? SemanticFieldSearchOperator.CONTAINS
            : SemanticFieldSearchOperator.EQUALS,
        valore: value,
      },
    ];
  }

  if (
    semanticTag.elementoRicerca ===
    EnumTagSemanticoDTOElementoRicerca.RICERCA_PER_INTERVALLO
  ) {
    const fromName = searchableFieldFromName(semanticTag.nome);
    const toName = searchableFieldToName(semanticTag.nome);
    const result = [];

    if (!isEmptySearchValue(values[fromName])) {
      result.push({
        chiave: semanticTag.id,
        operatore: SemanticFieldSearchOperator.GREATER_OR_EQUAL,
        valore: values[fromName],
      });
    }
    if (!isEmptySearchValue(values[toName])) {
      result.push({
        chiave: semanticTag.id,
        operatore: SemanticFieldSearchOperator.LESSER_OR_EQUAL,
        valore: values[toName],
      });
    }

    return result;
  }

  return [];
}

/**
 * Funzione di format che trasforma l'oggetot con i filtri di ricerca nella query da inviare al server
 * { [semanticTag]: ..., [semanticTag]: ..., <others>} -> { <others>, campiSemantici: [{chiave: 'semanticTag', operatore: <...>, valore: ...}]}
 */
export function formatSemanticTagSearchable<
  T extends { campiSemantici?: string },
>(
  values: Record<string, any>,
  semanticTags: TagSemanticoDTO[] | null | undefined,
): T {
  // Recupero la lista dei tag semantici esistenti (i campi di tipo intervallo hanno 2 chiavi separate)
  const semanticTagKeys =
    semanticTags
      ?.map(tag => {
        if (
          tag.elementoRicerca ===
          EnumTagSemanticoDTOElementoRicerca.RICERCA_PER_INTERVALLO
        ) {
          return [
            searchableFieldFromName(tag.nome),
            searchableFieldToName(tag.nome),
          ];
        }
        return [tag.nome];
      })
      .flatMap(t => t)
      .filter(t => Boolean(t)) ?? ([] as string[]);

  const staticValues: Record<string, any> = {};

  // Tengo solo i campi _non semantici_ (statici), la lista dei campi semantici
  // viene ricostruita a parte in `semanticTagQueryList`
  for (const key in values) {
    if (!semanticTagKeys.includes(key)) {
      staticValues[key] = values[key];
    }
  }

  // Creo la lista dei campi semantici
  const semanticTagQueryList =
    semanticTags
      ?.map(tag => {
        return getSemanticTagFilterOperator(tag, values).flatMap(f => f);
      })
      .flatMap(f => f) ?? [];

  const result = { ...staticValues } as T;

  // Emetto `campiSemantici` solo se ci sono filtri semantici valorizzati, così
  // da non sporcare l'URL (e non attivare il banner "filtri attivi") con una
  // lista vuota `"[]"`. Il parse gestisce già l'assenza della chiave.
  if (semanticTagQueryList.length > 0) {
    // NB: anche se indicato come un array di oggetti, l'endpoint specifica nella descrizione
    // che è richiesto come stringa in formato JSON.
    result.campiSemantici = JSON.stringify(semanticTagQueryList);
  }

  return result;
}
